import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TornDto } from '@/api/planificacio';
import { Desplegable } from '@/components/Desplegable';
import { Icona, type NomIcona } from '@/components/Icona';
import { Avatar, Camp, Modal } from '@/pages/operacio/comu';
import { aLocalInput, deLocalInput } from './calendari';

export interface Persona {
  id: number;
  nom: string;
}

const CANALS: { canal: 'PUSH' | 'EMAIL' | 'TRUCADA'; icona: NomIcona }[] = [
  { canal: 'PUSH', icona: 'bell' },
  { canal: 'EMAIL', icona: 'envelope' },
  { canal: 'TRUCADA', icona: 'phone' },
];

export function W12ModalTorn({
  torn,
  calendari,
  ambit,
  persones,
  onDesa,
  onElimina,
  onTanca,
  errorElimina,
  eliminant,
}: {
  torn: TornDto;
  calendari: string;
  ambit: string;
  persones: Persona[];
  onDesa: (t: TornDto) => void;
  onElimina?: () => void;
  onTanca: () => void;
  /** Error de l'eliminació al servidor (el diàleg es manté obert per mostrar-lo). */
  errorElimina?: string | null;
  eliminant?: boolean;
}) {
  const { t } = useTranslation('planificacio');
  const { t: te } = useTranslation('errors');
  const [inici, setInici] = useState(aLocalInput(torn.inici));
  const [fi, setFi] = useState(aLocalInput(torn.fi));
  const [titularId, setTitularId] = useState<number | ''>(torn.titularId || '');
  const [substitutId, setSubstitutId] = useState<number | ''>(torn.substitutId ?? '');
  const [notes, setNotes] = useState(torn.notes ?? '');

  const nom = (id: number | '') => persones.find((p) => p.id === id)?.nom ?? (id ? `#${id}` : '');
  let error: string | null = null;
  if (!inici || !fi || new Date(fi).getTime() <= new Date(inici).getTime()) {
    error = te('error.torn.dates', { keySeparator: false });
  } else if (titularId !== '' && titularId === substitutId) {
    error = te('error.torn.substitut.igual', { keySeparator: false });
  }

  const desa = () => {
    if (error || titularId === '') return;
    onDesa({
      ...torn,
      inici: deLocalInput(inici),
      fi: deLocalInput(fi),
      titularId,
      substitutId: substitutId === '' ? null : substitutId,
      notes: notes.trim() || null,
    });
  };

  const avisats = [nom(titularId), nom(substitutId)].filter(Boolean);

  return (
    <Modal
      screen="W-12"
      titol={torn.id == null ? t('torn.nou') : t('torn.editar')}
      icona={<Icona nom="calendar-clock" className="icon-20 c-blue" />}
      onTanca={onTanca}
      peu={
        <>
          {onElimina ? (
            <button
              type="button"
              className="btn btn--outline btn--s c-red"
              disabled={eliminant}
              onClick={onElimina}
            >
              <Icona nom="trash" />
              {t('torn.elimina')}
            </button>
          ) : null}
          <div className="toolbar__spacer" />
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('comu.cancella')}
          </button>
          <button
            type="button"
            className="btn btn--blue btn--s"
            disabled={!!error || titularId === ''}
            onClick={desa}
          >
            {t('torn.desa')}
          </button>
        </>
      }
    >
      <div className="grid grid-2" style={{ gap: 12 }}>
        <Camp etiqueta={t('torn.calendari')}>
          <input className="input" value={calendari} readOnly />
        </Camp>
        <Camp etiqueta={t('torn.ambit')}>
          <input className="input" value={ambit} readOnly />
        </Camp>
        <Camp etiqueta={t('torn.inici')}>
          <input
            className="input"
            type="datetime-local"
            value={inici}
            onChange={(e) => setInici(e.target.value)}
          />
        </Camp>
        <Camp etiqueta={t('torn.fi')}>
          <input
            className="input"
            type="datetime-local"
            value={fi}
            onChange={(e) => setFi(e.target.value)}
          />
        </Camp>
        <Camp etiqueta={t('torn.titular')}>
          <span className="hstack gap-8">
            <Avatar nom={titularId === '' ? null : nom(titularId)} />
            <Desplegable<number | ''>
              aria-label={t('torn.titular')}
              valor={titularId}
              onCanvia={setTitularId}
              opcions={[
                { valor: '', etiqueta: t('torn.triaPersona') },
                ...persones.map((p) => ({ valor: p.id, etiqueta: p.nom })),
              ]}
            />
          </span>
        </Camp>
        <Camp etiqueta={t('torn.substitut')}>
          <span className="hstack gap-8">
            <Avatar nom={substitutId === '' ? null : nom(substitutId)} />
            <Desplegable<number | ''>
              aria-label={t('torn.substitut')}
              valor={substitutId}
              onCanvia={setSubstitutId}
              opcions={[
                { valor: '', etiqueta: t('torn.senseSubstitut') },
                ...persones.map((p) => ({ valor: p.id, etiqueta: p.nom })),
              ]}
            />
          </span>
        </Camp>
      </div>
      <div className="camp">
        <span className="camp__label">{t('torn.canals')}</span>
        <div className="chips">
          {CANALS.map((c) => (
            <span key={c.canal} className="chip chip--blue">
              <Icona nom={c.icona} />
              {t(`canals.${c.canal}`)}
            </span>
          ))}
        </div>
      </div>
      <Camp etiqueta={t('torn.notes')}>
        <textarea
          className="input"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Camp>
      {error ? (
        <p className="error-text" style={{ margin: 0 }}>
          {error}
        </p>
      ) : avisats.length ? (
        <div className="hstack gap-8">
          <Icona nom="bell" className="icon-18 c-blue" />
          <span className="t-footnote dim">
            {t('torn.avis', { persones: avisats.join(` ${t('comu.i')} `) })}
          </span>
        </div>
      ) : null}
      {errorElimina ? (
        <p className="error-text" role="alert" style={{ margin: 0 }}>
          {errorElimina}
        </p>
      ) : null}
    </Modal>
  );
}
